// File: /pages/ShuffleMaps/MyListings.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import styles from "./MyListings.module.css";
import ListingCard from "/components/Shufflemaps/Listings/ListingCard";
import { useUser } from "/context/UserContext";

export default function MyListingsPage() {
  const { user, status } = useUser(); // status: 'loading' | 'authenticated' | 'unauthenticated' (shape may vary)
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const topRef = useRef(null);

  // Fetch all listings once; we'll filter by owner client-side
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/shufflemaps/listings/get-listings", {
          credentials: "include",
          cache: "no-store",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!alive) return;
        setAllListings(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load listings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const ownedListings = useMemo(() => {
    if (!user?.id) return [];
    const uid = Number(user.id);
    return (allListings || []).filter((l) => Number(l?.created_by) === uid);
  }, [allListings, user?.id]);

  const isLoadingUser = status === "loading";
  const notSignedIn = !user && status !== "loading";

  return (
    <main className={styles.smMain}>
      <section ref={topRef} className={styles.smSection}>
        <h1 className={styles.smTitle}>My Listings</h1>
        <p className={styles.smSub}>
          Listings you’ve claimed or created. Use the Create Listing flow to add more.
        </p>

        {isLoadingUser || loading ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>Loading…</div>
            <div className={styles.emptySub}>Fetching your listings.</div>
          </div>
        ) : errorMsg ? (
          <div className={styles.errorBox}>{errorMsg}</div>
        ) : notSignedIn ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>Sign in to continue</div>
            <div className={styles.emptySub}>
              You need to be signed in to view your claimed listings.
            </div>
            <div className={styles.actionsRow}>
              <Link href="/ShuffleMaps" className={styles.btnPrimary}>
                Go to ShuffleMaps
              </Link>
              <Link href="/ShuffleMaps/CreateListing" className={styles.btnGhost}>
                Create a Listing
              </Link>
            </div>
          </div>
        ) : ownedListings.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>No listings yet</div>
            <div className={styles.emptySub}>
              You haven’t claimed any listings. You can create a new one, or find an existing listing and claim it.
            </div>
            <div className={styles.actionsRow}>
              <Link href="/ShuffleMaps/CreateListing" className={styles.btnPrimary}>
                Create a Listing
              </Link>
              <Link href="/ShuffleMaps" className={styles.btnGhost}>
                Explore Listings
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.resultsHeader}>
              <div className={styles.resultsTitle}>
                {ownedListings.length} {ownedListings.length === 1 ? "listing" : "listings"}
              </div>
            </div>

            <div className={styles.cardsGrid}>
              {ownedListings.map((l) => (
                <Link
                  key={l.id}
                  href={{ pathname: "/ShuffleMaps", query: { listingId: String(l.id) } }}
                  className={styles.cardWrap}
                  title="Open in ShuffleMaps"
                >
                  <ListingCard
                    listingId={l.id}
                    name={l.name}
                    status={l.status}
                    shortDescription={l.short_description ?? l.shortDescription}
                    activeThumbnailUrl={l.active_thumbnail_url ?? l.activeThumbnailUrl ?? null}
                    suppressEmptyRatingDisplay
                  />
                </Link>
              ))}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
